import {
  Container,
  InteractionEvent,
  Sprite,
  WRAP_MODES,
  filters,
  Circle,
} from "pixi.js";
import { singleton } from "tsyringe";
import { EventBus } from "~/src/event-bus";
import { PixiApp } from "~/src/pixi-app";
import gsap from "gsap";
import { GlowFilter } from "pixi-filters";
import clamp from "lodash/clamp";
import { MAIN_COLOR } from "~/src/config/colors";

const GLOW_STRENGTH_ACTIVE = 6;
const GLOW_STRENGTH_REST = 3;
const GLOW_ANIM_DUR = 0.75;
const DISP_SCALE = 20;
const DISP_PAD = 10;
const DISP_SPEED = 0.425;
const DISP_SPEED_MAX = 25;
const DISP_SCALE_MAX = 90;
const SPEED_FACTOR = 10;
const SCALE_FACTOR = 20;
const DRAG_TWEEN_DUR = 0.2;
const BASE_SCALE = 0.5;
const STRETCH_FACTOR = 0.15;
const MAX_STRETCH = 0.3;
// const y = document.documentElement.clientHeight - 100;
@singleton()
export class DraggerComponent {
  private isDragging = false;
  isMorphing = false;
  private isHovered = false;
  private glowTween: gsap.core.Tween;
  private dispScaleTween: gsap.core.Tween;
  private dragger: Sprite;
  private dispFilter: any;
  private dispSprite: Sprite;
  private glow: GlowFilter;
  private currentDispSpeed = DISP_SPEED;
  private currentDispScale = 0;
  private prevPointerX = 0;
  private prevPointerTime = 0;
  private speedTween: gsap.core.Tween;
  private scaleTween: gsap.core.Tween;
  constructor(private eventBus: EventBus, private pixiApp: PixiApp) {}
  mount(container: Container) {
    const dragger = new Sprite(this.pixiApp.loader.resources.circle.texture!);
    dragger.scale.set(0.5, 0.5);
    this.dragger = dragger;
    this.createGlow();
    this.createDisplacementFilter();
    dragger.interactive = true;
    dragger.anchor.set(0.5, 0.5);
    dragger.hitArea = new Circle(0, 0, dragger.width);
    dragger.cursor = "grab";
    dragger.y = document.documentElement.clientHeight - dragger.height / 2 - 75;
    dragger.filters = [this.dispFilter, this.glow];
    dragger.x = document.documentElement.clientWidth / 2;
    dragger.addChild(this.dispSprite);
    container.addChild(dragger);
    this.setupEvents();
  }
  disableInteraction() {
    this.dragger.interactive = false;
  }
  enableInteraction() {
    this.dragger.interactive = true;
  }
  playEnterAnimation() {
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: GLOW_STRENGTH_REST,
      innerStrength: GLOW_STRENGTH_REST,
      duration: 1.5,
    });
  }
  nilGlowStrength() {
    this.glow.innerStrength = 0;
    this.glow.outerStrength = 0;
  }
  private onTick = () => {
    this.dispSprite.x += this.currentDispSpeed;
    this.dispFilter.scale.set(this.currentDispScale, DISP_SCALE * 0.4);
  };
  private enableMorphing() {
    if (this.isMorphing) return;
    this.isMorphing = true;
    this.pixiApp.ticker.add(this.onTick);
    this.dispScaleTween?.kill();
    this.dispScaleTween = gsap.to(this, {
      currentDispScale: DISP_SCALE,
      duration: 0.5,
      ease: "power1.out",
      overwrite: true,
    });
  }
  private disableMorphing() {
    this.isMorphing = false;
    this.speedTween?.kill();
    this.scaleTween?.kill();
    this.dispScaleTween?.kill();
    this.dispScaleTween = gsap.to(this, {
      currentDispScale: 0,
      duration: 0.5,
      ease: "power1.out",
      onComplete: () => {
        if (!this.isMorphing) this.pixiApp.ticker.remove(this.onTick);
      },
    });
  }

  private createDisplacementFilter() {
    const sprite = new Sprite(this.pixiApp.loader.resources.textDisp.texture!);
    sprite.texture.baseTexture.wrapMode = WRAP_MODES.MIRRORED_REPEAT;
    const filter = new filters.DisplacementFilter(sprite);
    this.dispFilter = filter;
    this.dispSprite = sprite;
    filter.padding = DISP_PAD;
    filter.scale.set(0, 0);
  }
  private createGlow() {
    const glow = new GlowFilter({
      color: MAIN_COLOR,
      knockout: true,
      outerStrength: 0,
      innerStrength: 0,
      quality: 1,
    });
    glow.padding = 25;
    this.glow = glow;
  }
  private setupEvents() {
    this.eventBus.on("resize", this.onResize);
    this.dragger
      .on("pointerover", this.onPointerOver)
      .on("pointerout", this.onPointerOut)
      .on("pointerdown", this.onPointerDown)
      .on("pointerup", this.onPointerUp)
      .on("pointerupoutside", this.onPointerUp);
  }
  private onPointerMove = (e: InteractionEvent) => {
    const viewportWidth = document.documentElement.clientWidth;

    const x = clamp(e.data.global.x, 0, viewportWidth);
    const y = clamp(e.data.global.y, 0, document.documentElement.clientHeight);
    const progress = x / viewportWidth;
    this.eventBus.emit("dragger/progress", progress);
    gsap.to(this.dragger, {
      x,
      y,
    });

    const now = performance.now();
    const dt = now - this.prevPointerTime;
    const dx = x - this.prevPointerX;
    const velocity = dt > 0 ? dx / dt : 0;
    this.prevPointerX = x;
    this.prevPointerTime = now;

    const absVelocity = Math.abs(velocity);
    const isInDeadzone = absVelocity < 0.15;
    const effectiveVelocity = isInDeadzone ? 0 : velocity;

    const targetSpeed = clamp(
      effectiveVelocity * SPEED_FACTOR,
      -DISP_SPEED_MAX,
      DISP_SPEED_MAX
    );
    const targetScale = clamp(
      DISP_SCALE + absVelocity * SCALE_FACTOR,
      DISP_SCALE,
      DISP_SCALE_MAX
    );

    const stretch = clamp(absVelocity * STRETCH_FACTOR, 0, MAX_STRETCH);
    const targetScaleX = BASE_SCALE + stretch;
    const targetScaleY = BASE_SCALE - stretch * 0.6;

    this.speedTween?.kill();
    this.scaleTween?.kill();
    this.speedTween = gsap.to(this, {
      currentDispSpeed: targetSpeed,
      duration: DRAG_TWEEN_DUR,
      overwrite: true,
    });
    this.scaleTween = gsap.to(this, {
      currentDispScale: targetScale,
      duration: DRAG_TWEEN_DUR,
      overwrite: true,
    });
    gsap.to(this.dragger.scale, {
      x: targetScaleX,
      y: targetScaleY,
      duration: DRAG_TWEEN_DUR,
      overwrite: true,
    });
  };
  private onResize = () => {
    const { clientWidth, clientHeight } = document.documentElement;
    const { x, y } = this.dragger;
    if (x <= 0 || x >= clientWidth) {
      this.dragger.x = clientWidth / 2;
    }
    if (y <= 0 || y >= clientHeight) {
      this.dragger.y = clientHeight - this.dragger.height / 2 - 75;
    }
  };
  private onPointerDown = () => {
    this.enableMorphing();
    this.isDragging = true;
    this.eventBus.emit("dragger/grab");
    document.body.classList.add("dragging");
    this.dragger.cursor = "grabbing";
    this.dragger.on("pointermove", this.onPointerMove);
    this.prevPointerX = this.dragger.x;
    this.prevPointerTime = performance.now();
    this.animateGlowActive();
  };
  private onPointerUp = () => {
    const stayingMorphed = this.isHovered;
    if (!stayingMorphed) {
      this.disableMorphing();
    }
    this.eventBus.emit("dragger/release");
    document.body.classList.remove("dragging");
    this.dragger.cursor = "grab";
    this.dragger.off("pointermove", this.onPointerMove);
    this.isDragging = false;
    if (!stayingMorphed) {
      this.animateGlowRest();
    }
    this.speedTween?.kill();
    this.scaleTween?.kill();
    gsap.to(this.dragger.scale, {
      x: BASE_SCALE,
      y: BASE_SCALE,
      duration: 0.5,
      ease: "power1.out",
      overwrite: true,
    });
    if (stayingMorphed) {
      this.speedTween = gsap.to(this, {
        currentDispSpeed: DISP_SPEED,
        duration: 0.4,
      });
      this.scaleTween = gsap.to(this, {
        currentDispScale: DISP_SCALE,
        duration: 0.4,
      });
    }
  };
  private animateGlowActive() {
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: GLOW_STRENGTH_ACTIVE,
      innerStrength: GLOW_STRENGTH_ACTIVE,
      duration: GLOW_ANIM_DUR,
      ease: "linear",
    });
  }
  private animateGlowRest() {
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: GLOW_STRENGTH_REST,
      innerStrength: GLOW_STRENGTH_REST,
      duration: GLOW_ANIM_DUR,
      ease: "linear",
    });
  }
  private onPointerOut = () => {
    this.isHovered = false;
    this.eventBus.emit("dragger/pointerOut");
    if (!this.isDragging) {
      this.disableMorphing();
      this.animateGlowRest();
    }
  };
  private onPointerOver = () => {
    this.isHovered = true;
    if (!this.isDragging) {
      this.enableMorphing();
    }
    this.animateGlowActive();
    this.eventBus.emit("dragger/pointerOver");
  };
}
